import { Children, isValidElement, type ReactElement, ReactNode } from 'react';

export interface TableData {
    headers: string[];
    rows: string[][];
}

export interface ColumnClassification {
    labelColumnIndex: number;
    numericColumnIndices: number[];
}

export interface ChartReadyData {
    labels: string[];
    datasets: Array<{
        label: string;
        data: (number | null)[];
        backgroundColor: string;
        borderColor: string;
        borderWidth: number;
        type?: 'bar' | 'line';
        tension?: number;
        fill?: boolean;
    }>;
}

export type InferredChartType = 'bar' | 'line';

const CHART_COLORS = [
    '#9975FF',
    '#FF8C22',
    '#4FC3F7',
    '#FF2629',
    '#65CC01',
    '#FFFF07',
    '#F06292',
    '#80CBC4',
];

const TIME_SERIES_PATTERNS = [
    /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
    /^(january|february|march|april|may|june|july|august|september|october|november|december)/i,
    /^\d{4}$/,
    /^q[1-4]\s*\d{0,4}$/i,
    /^\d{1,2}[/-]\d{1,2}([/-]\d{2,4})?$/,
    /^\d{4}-\d{2}(-\d{2})?$/,
    /^(fy|cy)\s*\d{2,4}$/i,
    /^(week|wk)\s*\d+/i,
];

/** Extract text content from a React node recursively. */
function getTextContent(node: ReactNode): string {
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }
    if (Array.isArray(node)) {
        return node.map(getTextContent).join('');
    }
    if (isValidElement(node)) {
        return getTextContent((node.props as { children?: ReactNode }).children);
    }
    return '';
}

function getTableCellText(cell: ReactElement): string {
    const cellProps = cell.props as { children?: ReactNode };
    return getTextContent(cellProps.children).trim();
}

/** Walk the react-markdown table child tree and extract header + row data. */
export function extractTableData(tableChildren: ReactNode): TableData {
    const headers: string[] = [];
    const rows: string[][] = [];

    Children.forEach(tableChildren, (section) => {
        if (!isValidElement(section)) {
            return;
        }
        const sectionType = (section.props as { children?: ReactNode });
        const sectionChildren = Children.toArray(sectionType.children);

        // thead or tbody
        const isHead = (section.type as string) === 'thead' || String(section.type).includes('head');

        sectionChildren.forEach((trNode) => {
            if (!isValidElement(trNode)) {
                return;
            }
            const trProps = trNode.props as { children?: ReactNode };
            const cells = Children.toArray(trProps.children);
            const cellTexts = cells.filter(isValidElement).map(getTableCellText);

            if (isHead) {
                headers.push(...cellTexts);
            } else {
                rows.push(cellTexts);
            }
        });
    });

    return { headers, rows };
}

/** Determine which columns contain numeric data and which is the label column. */
export function classifyColumns(headers: string[], rows: string[][]): ColumnClassification | null {
    if (headers.length === 0 || rows.length === 0) {
        return null;
    }

    const numericColumnIndices: number[] = [];
    let labelColumnIndex = -1;

    for (let col = 0; col < headers.length; col++) {
        const values = rows.map((row) => row[col] ?? '');
        const nonEmptyValues = values.filter((v) => v !== '');
        if (nonEmptyValues.length === 0) {
            continue;
        }

        const numericCount = nonEmptyValues.filter((v) => {
            const cleaned = v.replace(/[%$,]/g, '').trim();
            return cleaned !== '' && !isNaN(Number(cleaned));
        }).length;

        const numericRatio = numericCount / nonEmptyValues.length;
        if (numericRatio >= 0.7) {
            numericColumnIndices.push(col);
        } else if (labelColumnIndex === -1) {
            labelColumnIndex = col;
        }
    }

    // If no explicit label column was found, use first non-numeric column
    if (labelColumnIndex === -1) {
        for (let col = 0; col < headers.length; col++) {
            if (!numericColumnIndices.includes(col)) {
                labelColumnIndex = col;
                break;
            }
        }
    }

    if (labelColumnIndex === -1 || numericColumnIndices.length === 0) {
        return null;
    }

    return { labelColumnIndex, numericColumnIndices };
}

/** Infer whether a table is time-series (line) or categorical (bar). */
export function inferChartType(rows: string[][], labelColumnIndex: number): InferredChartType {
    const labelValues = rows.slice(0, 5).map((row) => (row[labelColumnIndex] ?? '').trim());
    const timeSeriesMatches = labelValues.filter((v) =>
        TIME_SERIES_PATTERNS.some((pattern) => pattern.test(v))
    );
    return timeSeriesMatches.length >= Math.ceil(labelValues.length * 0.5) ? 'line' : 'bar';
}

/** Parse a cell value to a number, stripping common formatting characters. */
function parseNumericValue(value: string): number | null {
    const cleaned = value.replace(/[%$,]/g, '').trim();
    if (cleaned === '' || isNaN(Number(cleaned))) {
        return null;
    }
    return Number(cleaned);
}

/** Build a Chart.js-ready dataset config from parsed table data. */
export function buildChartData(
    headers: string[],
    rows: string[][],
    classification: ColumnClassification,
    chartType: InferredChartType
): ChartReadyData {
    const { labelColumnIndex, numericColumnIndices } = classification;

    const labels = rows.map((row) => row[labelColumnIndex] ?? '');

    const datasets = numericColumnIndices.map((colIndex, i) => {
        const color = CHART_COLORS[i % CHART_COLORS.length];
        const data = rows.map((row) => parseNumericValue(row[colIndex] ?? ''));
        return {
            label: headers[colIndex] ?? `Series ${i + 1}`,
            data,
            backgroundColor: chartType === 'bar' ? color + 'CC' : color + '33',
            borderColor: color,
            borderWidth: 2,
            ...(chartType === 'line' ? { tension: 0.2, fill: false } : {}),
        };
    });

    return { labels, datasets };
}

/** Check whether a table has enough structure to render as a chart. */
export function isChartable(headers: string[], rows: string[][]): boolean {
    if (headers.length < 2 || rows.length < 2) {
        return false;
    }
    const classification = classifyColumns(headers, rows);
    return classification !== null;
}
